import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";

import { useFilters } from "../../../common/hooks/useFilters";
import FilterBar from "../../../components/FilterBar";
import { logError } from "../../../utils/error";
import { CONVERSATION_LENS } from "../../../utils/terminology";
import { getUserTimezone } from "../../../utils/timezone";
import { useAuth } from "../../auth/AuthContext";
import { useSelectedGroups } from "../../auth/hooks/useSelectedGroups";
import { useCancelBackgroundJob } from "../hooks/useBackgroundJobs";
import {
  CoachingArtifact,
  OrderBy,
  OrderDirection,
  useCoachingArtifactFilterOptions,
  useCoachingArtifacts,
  useDeleteCoachingArtifact
} from "../hooks/useCoachingArtifacts";
import { formatUserNameFromEmail } from "../utils/formatUserName";
import CoachingArtifactStatusChip from "./CoachingArtifactStatusChip";
import CoachingArtifactViewer from "./CoachingArtifactViewer";

// A coaching artifact that's still "generating" after an hour is treated as
// stalled. Underlying causes include lost/delayed Content Analysis webhooks,
// crashes mid-dispatch, or service outages — anything that leaves the row in
// "generating" without flipping it to "created" or "failed".
const STALLED_GENERATION_THRESHOLD_MS = 60 * 60 * 1000;

const isArtifactStalled = (artifact: CoachingArtifact): boolean => {
  if (artifact.status !== "generating") {
    return false;
  }
  const startedAt = new Date(artifact.created).getTime();
  if (Number.isNaN(startedAt)) {
    return false;
  }
  return Date.now() - startedAt > STALLED_GENERATION_THRESHOLD_MS;
};

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

const FAILED_TOOLTIP = "The report failed to generate, please try again later.";
const STALLED_TOOLTIP =
  "This report has been generating for over an hour and may be stuck. Cancel and try again.";

const ROW_CURSOR_POINTER_SX = { cursor: "pointer" } as const;
const ROW_CURSOR_DEFAULT_SX = { cursor: "default" } as const;

interface CreatedReportsProps {
  initialArtifactId?: string | null;
  onArtifactViewed?: () => void;
}

const CreatedReports = ({ initialArtifactId, onArtifactViewed }: CreatedReportsProps) => {
  const { tenantId } = useAuth();
  const { permittedEmails } = useSelectedGroups();
  const timezone = getUserTimezone();
  const cancelMutation = useCancelBackgroundJob({ tenantId: tenantId ?? "" });
  const deleteArtifactMutation = useDeleteCoachingArtifact();

  const [orderBy, setOrderBy] = useState<OrderBy>("created");
  const [order, setOrder] = useState<OrderDirection>("desc");
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [isViewerPanelOpen, setIsViewerPanelOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pendingActionArtifactId, setPendingActionArtifactId] = useState<string | null>(null);

  useEffect(() => {
    if (initialArtifactId) {
      setSelectedArtifactId(initialArtifactId);
      setIsViewerPanelOpen(true);
      onArtifactViewed?.();
    }
  }, [initialArtifactId, onArtifactViewed]);

  const { filters, updateFilter } = useFilters({
    defaults: {
      timeRange: "last7Days"
    }
  });

  useEffect(() => {
    setPage(0);
  }, [filters.timeRange, filters.user, filters.protocol]);

  const { data: filterOptions } = useCoachingArtifactFilterOptions({
    tenantId,
    timeRange: filters.timeRange,
    timezone,
    permittedEmails
  });

  const { data: paginatedData, isLoading, error } = useCoachingArtifacts({
    tenantId,
    timeRange: filters.timeRange,
    timezone,
    permittedEmails,
    page,
    pageSize: rowsPerPage,
    orderBy,
    orderDirection: order,
    userFilter: filters.user,
    lensFilter: filters.protocol
  });

  const artifacts = paginatedData?.data ?? [];
  const totalCount = paginatedData?.totalCount ?? 0;

  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (id: string) => {
    setSelectedArtifactId(id);
    setIsViewerPanelOpen(true);
  };

  const handleClosePanel = () => {
    setIsViewerPanelOpen(false);
  };

  const deleteArtifact = async (artifactId: string) => {
    if (!tenantId) {
      return;
    }
    try {
      await deleteArtifactMutation.mutateAsync({ artifactId, tenantId });
    } catch (err) {
      logError("Failed to delete coaching artifact", err);
    }
  };

  const handleCancel = async (artifactId: string, jobId: string) => {
    setPendingActionArtifactId(artifactId);
    try {
      await cancelMutation.mutateAsync(jobId);
    } catch (err) {
      logError("Failed to cancel coaching report job", err);
      await deleteArtifact(artifactId);
    } finally {
      setPendingActionArtifactId(null);
    }
  };

  const handleCancelFailed = async (artifactId: string) => {
    setPendingActionArtifactId(artifactId);
    await deleteArtifact(artifactId);
    setPendingActionArtifactId(null);
  };

  const isDataReady = !isLoading && !error;
  const showEmptyState = isDataReady && artifacts.length === 0;
  const showArtifactsTable = isDataReady && artifacts.length > 0;

  return (
    <Box sx={{ pb: 3 }}>
      <Box sx={{ mb: 1 }}>
        <FilterBar
          title="Created Reports"
          subTitle="View previously generated coaching reports"
          timeRange={filters.timeRange}
          protocol={filters.protocol || "all"}
          user={filters.user || "all"}
          sentiment={filters.sentiment || "all"}
          relevance={filters.relevance || "all"}
          risk={filters.risk || "all"}
          processDecision={filters.processDecision || "all"}
          callType={filters.callType || "all"}
          onFilterChange={updateFilter}
          showFilters={{
            timeRange: true,
            protocol: true,
            user: true,
            sentiment: false,
            relevance: false,
            risk: false,
            processDecision: false,
            callType: false,
            scoreRange: false,
            search: false
          }}
          sticky={false}
          customOptions={filterOptions}
        />
      </Box>

      {isLoading && (
        <Paper sx={{ p: 3, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Paper>
      )}

      {error && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">
            Failed to load reports: {error.message}
          </Alert>
        </Paper>
      )}

      {showEmptyState && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="info">
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              No reports found
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {totalCount === 0
                ? "Generate your first coaching report using the Quick Actions tab."
                : "Try adjusting your filter criteria."}
            </Typography>
          </Alert>
        </Paper>
      )}

      {showArtifactsTable && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "title"}
                    direction={orderBy === "title" ? order : "asc"}
                    onClick={() => handleSort("title")}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "created"}
                    direction={orderBy === "created" ? order : "asc"}
                    onClick={() => handleSort("created")}
                  >
                    Generated
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "start_date"}
                    direction={orderBy === "start_date" ? order : "asc"}
                    onClick={() => handleSort("start_date")}
                  >
                    Date Range
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "agent_email"}
                    direction={orderBy === "agent_email" ? order : "asc"}
                    onClick={() => handleSort("agent_email")}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "lens_name"}
                    direction={orderBy === "lens_name" ? order : "asc"}
                    onClick={() => handleSort("lens_name")}
                  >
                    {CONVERSATION_LENS}
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {artifacts.map((artifact) => {
                const isClickable = artifact.status === "created";
                const stalled = isArtifactStalled(artifact);
                const canCancel = artifact.status === "generating" && !!artifact.job_id;
                const canDismiss = artifact.status === "failed";
                const hasRowAction = canCancel || canDismiss;
                const isFailed = artifact.status === "failed";
                const statusChip = (
                  <CoachingArtifactStatusChip
                    status={artifact.status}
                    isStalled={stalled}
                  />
                );
                const renderStatusCell = () => {
                  if (isFailed) {
                    return (
                      <Tooltip title={FAILED_TOOLTIP}>
                        <span data-testid={`created-reports-failure-${artifact.id}`}>
                          {statusChip}
                        </span>
                      </Tooltip>
                    );
                  }
                  if (stalled) {
                    return (
                      <Tooltip title={STALLED_TOOLTIP}>
                        <span data-testid={`created-reports-stalled-${artifact.id}`}>
                          {statusChip}
                        </span>
                      </Tooltip>
                    );
                  }
                  return statusChip;
                };
                return (
                  <TableRow
                    key={artifact.id}
                    hover={isClickable}
                    onClick={isClickable ? () => handleRowClick(artifact.id) : undefined}
                    sx={isClickable ? ROW_CURSOR_POINTER_SX : ROW_CURSOR_DEFAULT_SX}
                    data-testid={`created-reports-row-${artifact.id}`}
                  >
                    <TableCell>
                      {artifact.content?.title || "-"}
                    </TableCell>
                    <TableCell>
                      {new Date(artifact.created).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {new Date(artifact.start_date).toLocaleDateString()} -{" "}
                      {new Date(artifact.end_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {formatUserNameFromEmail(artifact.agent_email)}
                    </TableCell>
                    <TableCell>{artifact.lens_name}</TableCell>
                    <TableCell onClick={hasRowAction ? stopPropagation : undefined}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {renderStatusCell()}
                        {canCancel && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={() => handleCancel(artifact.id, artifact.job_id as string)}
                            disabled={pendingActionArtifactId === artifact.id}
                            data-testid={`created-reports-cancel-${artifact.id}`}
                          >
                            {pendingActionArtifactId === artifact.id ? "Canceling…" : "Cancel"}
                          </Button>
                        )}
                        {canDismiss && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={() => handleCancelFailed(artifact.id)}
                            disabled={pendingActionArtifactId === artifact.id}
                            data-testid={`created-reports-cancel-failed-${artifact.id}`}
                          >
                            {pendingActionArtifactId === artifact.id ? "Canceling…" : "Cancel"}
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
      )}

      <Drawer
        anchor="right"
        open={isViewerPanelOpen}
        onClose={handleClosePanel}
        sx={{
          width: "75%",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: "75%",
            boxSizing: "border-box",
            zIndex: (theme) => theme.zIndex.drawer + 2,
            transition: "transform 0.3s ease-in-out"
          }
        }}
        transitionDuration={300}
      >
        <Box sx={{ mt: 8 }}>
          {selectedArtifactId && (
            <CoachingArtifactViewer
              artifactId={selectedArtifactId}
              tenantId={tenantId}
              permittedEmails={permittedEmails}
              isDrawer={true}
              onClose={handleClosePanel}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default CreatedReports;
